import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { highlightText } from "components/utils/SearchHighlight";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { FaTrashAlt, FaEye, FaEyeSlash } from "react-icons/fa";

interface ReleaseNotes {
  features?: string[];
  fixed?: string[];
  changed?: string[];
  deprecated?: string[];
  removed?: string[];
}

interface ApkInfo {
  version_code: string;
  version_name: string;
  release_date: string;
  release_type: string;
  apkfilename: string;
  mandatory_release: boolean;
  release_notes?: ReleaseNotes; // Add this
}

interface FormState {
  org_uid: string;
  version_code: string;
  version_name: string;
  release_type: string;
  release_date: string;
  mandatory_release: string;
  app_name: string;
  package_name: string;
  build_type: string;
  build_date: string;
  build_system: string;
  release_notes: {
    features: string[];
    fixed: string[];
    changed: string[];
    deprecated: string[];
    removed: string[];
  };
}
const IMask = require("imask");

const AppConfigurationPage: React.FC = () => {
  useCheckSessionHeartBeat();
  const [apkList, setApkList] = useState<ApkInfo[]>([]);
  const [filteredList, setFilteredList] = useState<ApkInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);

  const org_uid = localStorage.getItem("org_uid") || "";
  const user_uid = localStorage.getItem("user_uid") || "";
  const session_uid = localStorage.getItem("session_uid") || "";

  const [form, setForm] = useState<FormState>({
    org_uid: "",
    version_code: "",
    version_name: "",
    release_type: "",
    release_date: "",
    mandatory_release: "",
    app_name: "",
    package_name: "",
    build_type: "",
    build_date: "",
    build_system: "",
    release_notes: {
      features: [""],
      fixed: [""],
      changed: [""],
      deprecated: [""],
      removed: [""],
    },
  });
  type ArrayFields =
    | "features"
    | "fixed"
    | "changed"
    | "deprecated"
    | "removed";

  const handleArrayChange = (
    section: ArrayFields,
    index: number,
    value: string
  ) => {
    const updated = [...form.release_notes[section]];
    updated[index] = value;
    setForm({
      ...form,
      release_notes: { ...form.release_notes, [section]: updated },
    });
  };

  const addField = (section: ArrayFields) => {
    setForm({
      ...form,
      release_notes: {
        ...form.release_notes,
        [section]: [...form.release_notes[section], ""],
      },
    });
  };

  const removeField = (section: ArrayFields, index: number) => {
    const updated = [...form.release_notes[section]];
    updated.splice(index, 1);
    setForm({
      ...form,
      release_notes: { ...form.release_notes, [section]: updated },
    });
  };

  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedApk, setSelectedApk] = useState<ApkInfo | null>(null);
  const [orgList, setOrgList] = useState<{ org_uid: string }[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>(org_uid);

  useEffect(() => {
    fetchOrgList();
  }, []);

  useEffect(() => {
    if (selectedOrg) fetchApkInfo();
  }, [selectedOrg]);

  useEffect(() => {
    const filtered = apkList.filter(
      (apk) =>
        apk.apkfilename.toLowerCase().includes(search.toLowerCase()) ||
        apk.version_name.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredList(filtered);
  }, [search, apkList]);

  const fetchOrgList = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/org-info`,
        {
          headers: {
            "user-uid": user_uid,
          },withCredentials: true,
        }
      );

      const fetchedOrgs = response.data?.data || [];
      if (
        fetchedOrgs.length > 1 ||
        fetchedOrgs.some((o: any) => o.org_uid !== org_uid)
      ) {
        setIsSystemAdmin(true);
      }

      const selfOrg = { org_uid };
      const allOrgs = [
        selfOrg,
        ...fetchedOrgs.filter((o: any) => o.org_uid !== org_uid),
      ];
      setOrgList(allOrgs);
    } catch (error) {
      console.error("Error fetching orgs", error);
    }
  };

  const fetchApkInfo = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/get-apk-info`,
        {
          headers: {
            "user-uid": user_uid,
          },withCredentials: true,
          params: { org_uid: selectedOrg },
        }
      );
      const responseData = response.data?.data;
      setApkList(Array.isArray(responseData) ? responseData : []);
    } catch (error) {
      console.error("Error fetching APK info", error);
      setApkList([]);
    } finally {
      setLoading(false);
    }
  };

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleDetails = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);

  const toggleDropdown = (index: number) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const isReleaseNotesEmpty = (notes?: ReleaseNotes) => {
    if (!notes) return true;
    return (
      (notes.features?.length || 0) === 0 &&
      (notes.fixed?.length || 0) === 0 &&
      (notes.changed?.length || 0) === 0 &&
      (notes.deprecated?.length || 0) === 0 &&
      (notes.removed?.length || 0) === 0
    );
  };

  const handleUpload = async () => {
    setSubmitAttempted(true);

    const isMissingRequired =
      !form.version_code ||
      !form.version_name ||
      !form.release_type ||
      !form.release_date ||
      !form.mandatory_release ||
      !form.app_name ||
      !file ||
      (isSystemAdmin && !form.org_uid);

    if (isMissingRequired) return;

    const formData = new FormData();
    formData.append("file", file);

    formData.append(
      "metadata",
      JSON.stringify({
        ...form,
        org_uid: isSystemAdmin ? form.org_uid : org_uid,
      })
    );

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/apk/apk-upload`,
        formData,
        {
          headers: {
            "user-uid": user_uid,
          },withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(percent);
          },
        }
      );

      setShowUploadModal(false);
      setSubmitAttempted(false);
      setUploadProgress(0);
      setForm({
        org_uid: org_uid,
        version_code: "",
        version_name: "",
        release_type: "",
        release_date: "",
        mandatory_release: "",
        app_name: "",
        package_name: "",
        build_type: "",
        build_date: "",
        build_system: "",

        release_notes: {
          features: [""],
          fixed: [""],
          changed: [""],
          deprecated: [""],
          removed: [""],
        },
      });
      setFile(null);

      fetchApkInfo();
    } catch (error) {
      console.error("Upload failed", error);
    }
  };
  const handleDeleteClick = (apk: ApkInfo) => {
    setSelectedApk(apk);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedApk) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/v1/delete-apk`, {
        headers: { "user-uid": user_uid },
        withCredentials: true,
        params: {
          org_uid: isSystemAdmin ? selectedOrg : org_uid,
          apkfilename: selectedApk.apkfilename,
          releasetype: selectedApk.release_type,
          version_code: selectedApk.version_code,
        },
      });
      setShowConfirm(false);
      setSelectedApk(null);
      fetchApkInfo();
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const resetUploadForm = () => {
    setForm({
      org_uid: org_uid,
      version_code: "",
      version_name: "",
      release_type: "",
      release_date: "",
      mandatory_release: "",
      app_name: "",
      package_name: "",
      build_type: "",
      build_date: "",
      build_system: "",

      release_notes: {
        features: [""],
        fixed: [""],
        changed: [""],
        deprecated: [""],
        removed: [""],
      },
    });
    setFile(null);
    setUploadProgress(0);
    setSubmitAttempted(false);
  };

  useEffect(() => {
    const input = document.querySelector(".date-input");
    if (input) {
      IMask(input, {
        mask: "0000-00-00 00:00", // yyyy-mm-dd hh:mm
      });
    }
  }, []);
  return (
    <Container>
      <Header>
        <SearchInput
          type="text"
          placeholder="Search by version or filename..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <OrgSelect
          value={selectedOrg || ""}
          onChange={(e) => setSelectedOrg(e.target.value)}
        >
          <option value="" disabled hidden>
            Choose an Org
          </option>
          {orgList.map((org, idx) => (
            <option key={idx} value={org.org_uid}>
              {org.org_uid}
            </option>
          ))}
        </OrgSelect>

        <UploadBtn onClick={() => setShowUploadModal(true)}>
          Upload APK
        </UploadBtn>
      </Header>

      {loading ? (
        <p>Loading...</p>
      ) : filteredList.length === 0 ? (
        <Empty>No APK records found.</Empty>
      ) : (
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <Th>APK Name</Th>
                <Th>Version Code</Th>
                <Th>Release Type</Th>
                <Th>Release Date</Th>
                <Th>File Name</Th>
                <Th>Mandatory Release</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {[...filteredList]
                .sort(
                  (a, b) =>
                    new Date(b.release_date).getTime() -
                    new Date(a.release_date).getTime()
                )
                .map((apk, index) => (
                  <React.Fragment key={index}>
                    <tr>
                      <Td>{highlightText(apk.version_name, search)}</Td>
                      <Td>{highlightText(apk.version_code, search)}</Td>
                      <Td>{apk.release_type}</Td>
                      <Td>
                        {new Date(apk.release_date).toLocaleDateString([], {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}{" "}
                        {new Date(apk.release_date).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Td>
                      <Td>{highlightText(apk.apkfilename, search)}</Td>
                      <Td>
                        {highlightText(
                          apk.mandatory_release ? "Yes" : "No",
                          search
                        )}
                      </Td>
                      {/* <Td>
                        <DeleteBtn onClick={() => handleDeleteClick(apk)}>
                          Delete
                        </DeleteBtn>
                        <DetailsBtn onClick={() => toggleDetails(index)}>
                          {openIndex === index
                            ? "Hide Details"
                            : "View Details"}
                        </DetailsBtn>
                      </Td> */}

                      <Td>
                        <ActionIcons>
                          {/* View / Hide Details */}
                          <IconBtn
                            title={openIndex === index ? "Hide Details" : "View Details"}
                            onClick={() => toggleDetails(index)}
                          >
                            {openIndex === index ? <FaEyeSlash /> : <FaEye />}
                          </IconBtn>

                          {/* Delete */}
                          <IconBtn
                            title="Delete APK"
                            danger
                            onClick={() => handleDeleteClick(apk)}
                          >
                            <FaTrashAlt />
                          </IconBtn>
                        </ActionIcons>
                      </Td>
                    </tr>

                    {openIndex === index && (
                      <tr>
                        <Td colSpan={6}>
                          <ReleaseNotesBox>
                            <h4 style={{ margin: "5px 0" }}>Release Notes</h4>
                            {apk.release_notes ? (
                              <>
                                <p>
                                  <b>Features:</b>{" "}
                                  {apk.release_notes.features?.length
                                    ? apk.release_notes.features.join(", ")
                                    : ""}
                                </p>
                                <p>
                                  <b>Fixed:</b>{" "}
                                  {apk.release_notes.fixed?.length
                                    ? apk.release_notes.fixed.join(", ")
                                    : ""}
                                </p>
                                <p>
                                  <b>Changed:</b>{" "}
                                  {apk.release_notes.changed?.length
                                    ? apk.release_notes.changed.join(", ")
                                    : ""}
                                </p>
                                <p>
                                  <b>Deprecated:</b>{" "}
                                  {apk.release_notes.deprecated?.length
                                    ? apk.release_notes.deprecated.join(", ")
                                    : ""}
                                </p>
                                <p>
                                  <b>Removed:</b>{" "}
                                  {apk.release_notes.removed?.length
                                    ? apk.release_notes.removed.join(", ")
                                    : ""}
                                </p>
                              </>
                            ) : (
                              <p>No release notes available.</p>
                            )}
                          </ReleaseNotesBox>
                        </Td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
            </tbody>
          </StyledTable>
        </TableWrapper>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <ModalOverlay>
          <ModalBox>
            <h3 style={{ marginTop: "10px" }}>Upload New APK</h3>

            {isSystemAdmin && (
              <>
                <Label>
                  Select Organization<Required>*</Required>
                </Label>
                <FormSelect
                  name="org_uid"
                  value={form.org_uid || selectedOrg}
                  onChange={handleFormChange}
                  invalid={submitAttempted && !form.org_uid}
                >
                  <option value="">Select Org</option>
                  {orgList.map((org, idx) => (
                    <option key={idx} value={org.org_uid}>
                      {org.org_uid}
                    </option>
                  ))}
                </FormSelect>
              </>
            )}

            <FormRow>
              <FormRow>
                <FormItem>
                  <Label>
                    App Name<Required>*</Required>
                  </Label>
                  <FormInput
                    name="app_name"
                    placeholder="Enter App Code"
                    value={form.app_name}
                    onChange={handleFormChange}
                    invalid={submitAttempted && !form.app_name}
                  />
                </FormItem>

                <FormItem>
                  <Label>
                    Package Name<Required>*</Required>
                  </Label>
                  <FormInput
                    name="package_name"
                    placeholder="Enter Package Name"
                    value={form.package_name}
                    onChange={handleFormChange}
                    invalid={submitAttempted && !form.package_name}
                  />
                </FormItem>

                <FormItem>
                  <div style={{ width: "97%" }}>
                    <Label>
                      Release Type<Required>*</Required>
                    </Label>
                    <FormSelect
                      name="release_type"
                      value={form.release_type}
                      onChange={handleFormChange}
                      invalid={submitAttempted && !form.release_type}
                    >
                      <option value="">Select Type</option>
                      <option value="stable">Stable</option>
                      <option value="beta">Beta</option>
                    </FormSelect>
                  </div>
                </FormItem>
              </FormRow>
            </FormRow>

            <FormRow>
              <FormRow>
                <FormItem>
                  <div style={{ width: "97%" }}>
                    <Label>
                      Build Type<Required>*</Required>
                    </Label>
                    <FormSelect
                      name="build_type"
                      value={form.build_type}
                      onChange={handleFormChange}
                      invalid={submitAttempted && !form.build_type}
                    >
                      <option value="">Select Build Type</option>
                      <option value="stable">Stable</option>
                      <option value="beta">Beta</option>
                    </FormSelect>
                  </div>
                </FormItem>

                <FormItem>
                  <Label>
                    Build System<Required>*</Required>
                  </Label>
                  <FormInput
                    name="build_system"
                    placeholder="Enter Build System"
                    value={form.build_system}
                    onChange={handleFormChange}
                    invalid={submitAttempted && !form.build_system}
                  />
                </FormItem>

                <FormItem>
                  <Label>
                    Build Date<Required>*</Required>
                  </Label>
                  <Flatpickr
                    value={form.build_date || undefined}
                    options={{
                      enableTime: true,
                      time_24hr: true,
                      dateFormat: "Y-m-d H:i",
                      allowInput: true,
                      defaultHour: 0,
                      defaultMinute: 0,
                    }}
                    onChange={([date]) => {
                      setForm((prev) => ({
                        ...prev,
                        build_date: date ? date.toISOString() : "",
                      }));
                    }}
                    placeholder="yyyy-mm-dd hh:mm"
                    className="form-input"
                    style={{ width: "73%" }}
                  />
                </FormItem>
              </FormRow>
            </FormRow>

            <FormRow>
              <FormItem>
                <Label>
                  Version Code<Required>*</Required>
                </Label>
                <FormInput
                  name="version_code"
                  placeholder="Enter Version Code"
                  value={form.version_code}
                  onChange={handleFormChange}
                  invalid={submitAttempted && !form.version_code}
                />
              </FormItem>
              <FormItem>
                <Label>
                  Version Name<Required>*</Required>
                </Label>
                <FormInput
                  name="version_name"
                  placeholder="Enter Version Name"
                  value={form.version_name}
                  onChange={handleFormChange}
                  invalid={submitAttempted && !form.version_name}
                />
              </FormItem>
              <FormItem>
                <Label>
                  Select Release Date<Required>*</Required>
                </Label>
                <Flatpickr
                  value={form.release_date || undefined}
                  options={{
                    enableTime: true,
                    time_24hr: true,
                    dateFormat: "Y-m-d H:i",
                    allowInput: true,
                    defaultHour: 0,
                    defaultMinute: 0,
                  }}
                  onChange={([date]) => {
                    setForm((prev) => ({
                      ...prev,
                      release_date: date ? date.toISOString() : "",
                    }));
                  }}
                  placeholder="yyyy-mm-dd hh:mm"
                  className="form-input"
                  style={{ width: "73%" }}
                />
              </FormItem>
            </FormRow>

            <FormRow
              style={{ display: "flex", gap: "2rem", marginBottom: "1rem" }}
            >
              <FormItem style={{ flex: "0 0 31%" }}>
                {/* <div style={{ width: "100%" }}> */}
                <Label>
                  Mandatory Release<Required>*</Required>
                </Label>
                <FormSelect
                  name="mandatory_release"
                  value={form.mandatory_release}
                  onChange={handleFormChange}
                  invalid={submitAttempted && !form.mandatory_release}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </FormSelect>
                {/* </div> */}
              </FormItem>
              <FormItem style={{ flex: "0 0 33%" }}>
                <div style={{ width: "65%" }}>
                  <Label>
                    Choose APK File<Required>*</Required>
                  </Label>
                  <input
                    type="file"
                    accept=".apk"
                    onChange={(e) => {
                      const selectedFile = e.target.files?.[0] || null;
                      if (
                        selectedFile &&
                        !selectedFile.name.toLowerCase().endsWith(".apk")
                      ) {
                        alert("Only .apk files are allowed!");
                        e.target.value = ""; // clear invalid file
                        setFile(null);
                      } else {
                        setFile(selectedFile);
                      }
                    }}
                    style={{
                      border:
                        submitAttempted && !file
                          ? "1px solid red"
                          : "1px solid #ccc",
                      borderRadius: "6px",
                      padding: "0.6rem",
                      marginBottom: "0.6rem",
                    }}
                  />
                </div>
              </FormItem>
            </FormRow>
            <SectionWrapper>
              <SectionHeading>Release Notes</SectionHeading>
              <FieldsWrapper>
                <FormRow>
                  <FormItem style={{ flex: "0 0 115%" }}>
                    <Label>New Features</Label>
                    {form.release_notes.features.map((val, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <FormInput
                          name={`features${index}`}
                          placeholder="Enter New Feature"
                          value={val}
                          onChange={(e) =>
                            handleArrayChange("features", index, e.target.value)
                          }
                        />
                        <button
                          type="button"
                          onClick={() => addField("features")}
                          style={{
                            marginLeft: "6px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            border: "1px solid #4caf50",
                            backgroundColor: "#4caf50",
                            color: "white",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          +
                        </button>
                        {form.release_notes.features.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeField("features", index)}
                            style={{
                              marginLeft: "4px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              border: "1px solid #f44336",
                              backgroundColor: "#f44336",
                              color: "white",
                              fontWeight: "bold",
                              cursor: "pointer",
                            }}
                          >
                            -
                          </button>
                        )}
                      </div>
                    ))}
                  </FormItem>
                </FormRow>
                <FormRow>
                  <FormItem style={{ flex: "0 0 115%" }}>
                    <Label>Fixed Features</Label>
                    {form.release_notes.fixed.map((val, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <FormInput
                          name={`fixed${index}`}
                          placeholder="Enter Fixed Feature"
                          value={val}
                          onChange={(e) =>
                            handleArrayChange("fixed", index, e.target.value)
                          }
                        />
                        <button
                          type="button"
                          onClick={() => addField("fixed")}
                          style={{
                            marginLeft: "6px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            border: "1px solid #4caf50",
                            backgroundColor: "#4caf50",
                            color: "white",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          +
                        </button>
                        {form.release_notes.fixed.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeField("fixed", index)}
                            style={{
                              marginLeft: "4px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              border: "1px solid #f44336",
                              backgroundColor: "#f44336",
                              color: "white",
                              fontWeight: "bold",
                              cursor: "pointer",
                            }}
                          >
                            -
                          </button>
                        )}
                      </div>
                    ))}
                  </FormItem>
                </FormRow>
                <FormRow>
                  <FormItem style={{ flex: "0 0 115%" }}>
                    <Label>Changed Features</Label>
                    {form.release_notes.changed.map((val, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <FormInput
                          name={`changed${index}`}
                          placeholder="Enter Changed Feature"
                          value={val}
                          onChange={(e) =>
                            handleArrayChange("changed", index, e.target.value)
                          }
                        />
                        <button
                          type="button"
                          onClick={() => addField("changed")}
                          style={{
                            marginLeft: "6px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            border: "1px solid #4caf50",
                            backgroundColor: "#4caf50",
                            color: "white",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          +
                        </button>
                        {form.release_notes.changed.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeField("changed", index)}
                            style={{
                              marginLeft: "4px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              border: "1px solid #f44336",
                              backgroundColor: "#f44336",
                              color: "white",
                              fontWeight: "bold",
                              cursor: "pointer",
                            }}
                          >
                            -
                          </button>
                        )}
                      </div>
                    ))}
                  </FormItem>
                </FormRow>

                <FormRow>
                  <FormItem style={{ flex: "0 0 115%" }}>
                    <Label>Deprecated Features</Label>
                    {form.release_notes.deprecated.map((val, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <FormInput
                          name={`deprecated${index}`}
                          placeholder="Enter Deprecated Feature"
                          value={val}
                          onChange={(e) =>
                            handleArrayChange(
                              "deprecated",
                              index,
                              e.target.value
                            )
                          }
                        />
                        <button
                          type="button"
                          onClick={() => addField("deprecated")}
                          style={{
                            marginLeft: "6px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            border: "1px solid #4caf50",
                            backgroundColor: "#4caf50",
                            color: "white",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          +
                        </button>
                        {form.release_notes.deprecated.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeField("deprecated", index)}
                            style={{
                              marginLeft: "4px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              border: "1px solid #f44336",
                              backgroundColor: "#f44336",
                              color: "white",
                              fontWeight: "bold",
                              cursor: "pointer",
                            }}
                          >
                            -
                          </button>
                        )}
                      </div>
                    ))}
                  </FormItem>
                </FormRow>
                <FormRow>
                  <FormItem style={{ flex: "0 0 115%" }}>
                    <Label>Removed Features</Label>
                    {form.release_notes.removed.map((val, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <FormInput
                          name={`removed${index}`}
                          placeholder="Enter Removed Feature"
                          value={val}
                          onChange={(e) =>
                            handleArrayChange("removed", index, e.target.value)
                          }
                        />
                        <button
                          type="button"
                          onClick={() => addField("removed")}
                          style={{
                            marginLeft: "6px",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            border: "1px solid #4caf50",
                            backgroundColor: "#4caf50",
                            color: "white",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          +
                        </button>
                        {form.release_notes.removed.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeField("removed", index)}
                            style={{
                              marginLeft: "4px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              border: "1px solid #f44336",
                              backgroundColor: "#f44336",
                              color: "white",
                              fontWeight: "bold",
                              cursor: "pointer",
                            }}
                          >
                            -
                          </button>
                        )}
                      </div>
                    ))}
                  </FormItem>
                </FormRow>
              </FieldsWrapper>
            </SectionWrapper>

            {/* Upload Progress Bar */}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <ProgressBarWrapper>
                <ProgressBarFill style={{ width: `${uploadProgress}%` }} />
                <ProgressText>{uploadProgress}% uploaded</ProgressText>
              </ProgressBarWrapper>
            )}

            <ModalActions>
              <CancelBtn
                onClick={() => {
                  if (uploadProgress === 0 || uploadProgress === 100) {
                    resetUploadForm();

                    setShowUploadModal(false);
                  }
                }}
                disabled={uploadProgress > 0 && uploadProgress < 100}
                style={{
                  opacity: uploadProgress > 0 && uploadProgress < 100 ? 0.6 : 1,

                  cursor:
                    uploadProgress > 0 && uploadProgress < 100
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Cancel
              </CancelBtn>
              <ConfirmBtn
                onClick={handleUpload}
                disabled={uploadProgress > 0 && uploadProgress < 100}
                style={{
                  opacity: uploadProgress > 0 && uploadProgress < 100 ? 0.6 : 1,

                  cursor:
                    uploadProgress > 0 && uploadProgress < 100
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Upload
              </ConfirmBtn>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {/* {showConfirm && selectedApk && (
        <ModalOverlay>
          <ModalBox>
            <p>
              Are you sure you want to delete{" "}
              <strong>{selectedApk.apkfilename}</strong>?
            </p>
            <ModalActions>
              <CancelBtn onClick={() => setShowConfirm(false)}>
                Cancel
              </CancelBtn>
              <ConfirmBtn danger onClick={confirmDelete}>
                Delete
              </ConfirmBtn>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )} */}

      {showConfirm && selectedApk && (
        <ModalOverlay>
          <SmallModalBox>
            <p style={{ marginBottom: "1rem", textAlign: "center" }}>
              Are you sure you want to delete
              <br />
              <strong>{selectedApk.apkfilename}</strong>?
            </p>

            <ModalActions>
              <CancelBtn onClick={() => setShowConfirm(false)}>
                Cancel
              </CancelBtn>
              <ConfirmBtn danger onClick={confirmDelete}>
                Delete
              </ConfirmBtn>
            </ModalActions>
          </SmallModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AppConfigurationPage;

const Container = styled.div`
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
  background-color: var(--page-color);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const UploadBtn = styled.button`
  background-color: var(--sidebar-hover-color);
  color: var(--text-color);
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  width: 50%;
  border: 1px solid #ccc;
  border-radius: 6px;
  background:var(--card-background);
  color:var(--text-color);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--card-background);
  border-radius: 8px;
  overflow: hidden;
`;

const Th = styled.th`
  background-color: #f5f5f5;
  padding: 1rem;
  text-align: center;
  border-bottom: 1px solid #ddd;
`;

const Td = styled.td`
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #eee;
  color:var(--text-color);
  text-align: center;
  vertical-align: middle;
`;

const Empty = styled.p`
  text-align: center;
  margin-top: 2rem;
  font-size: 1rem;
  color: #888;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  overflow-y: auto;
`;

const ModalBox = styled.div`
  background: #fff;
  padding: 1.5rem;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 10px;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    width: 95%;
    padding: 1rem;
  }
`;

const FormInput = styled.input<{ invalid?: boolean }>`
  margin-top: 0.3rem;
  padding: 0.6rem;
  width: 80%;
  border: 1px solid ${({ invalid }) => (invalid ? "red" : "#ccc")};
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border 0.2s ease;

  &:focus {
    outline: none;
    border-color: #094752;
    box-shadow: 0 0 0 2px rgba(9, 71, 82, 0.15);
  }
`;

const FormSelect = styled.select<{ invalid?: boolean }>`
  margin-top: 0.3rem;
  padding: 0.6rem;
  width: 90%;
  border: 1px solid ${({ invalid }) => (invalid ? "red" : "#ccc")};
  border-radius: 6px;
  font-size: 0.95rem;
  background: white;
  transition: border 0.2s ease;

  &:focus {
    outline: none;
    border-color: #094752;
    box-shadow: 0 0 0 2px rgba(9, 71, 82, 0.15);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

const CancelBtn = styled.button`
  background: #ccc;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
`;

const ConfirmBtn = styled.button<{ danger?: boolean }>`
  background: ${({ danger }) =>
    danger ? "#ff4d4f" : "#4caf50"}; /* red if danger */
  padding: 0.6rem 1rem;
  border-radius: 6px;
  color: white;
  border: none;
  cursor: pointer;

  &:hover {
    background: ${({ danger }) => (danger ? "#d9363e" : "#43a047")};
  }
`;

const Label = styled.label`
  font-weight: 500;
  margin: 0.5rem 0 0.2rem 0;
  display: block;
  width: 80%;
`;

const DeleteBtn = styled.button`
  background-color: #ff4d4f;
  color: white;
  border: none;
  padding: 6px 12px;
  font-size: 0.9rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover {
    background-color: #d9363e;
  }
  &:active {
    background-color: #b52a2f;
  }
`;

const OrgSelect = styled.select`
  padding: 0.5rem;
  margin-left: 1rem;
  border: 1px solid #ccc;
  border-radius: 6px;
`;

const ProgressBarWrapper = styled.div`
  background: #eee;
  border-radius: 6px;
  height: 10px;
  margin-top: 1rem;
`;

const ProgressBarFill = styled.div`
  background: #4caf50;
  height: 100%;
  border-radius: 6px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.p`
  font-size: 0.8rem;
  margin-top: 0.3rem;
  text-align: center;
`;
const Spacer = styled.div`
  margin-top: 0.5rem;
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap; /* allow wrapping for smaller screens */
  gap: 1rem;
  margin-bottom: 1rem;
  width: 100%;
`;

// const FormItem = styled.div`
//   flex: 1;
//   min-width: 55%;
// `;
const FormItem = styled.div`
  flex: 1;
  min-width: 220px; /* ensures fields don’t shrink too much */
  display: flex;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  /* Optional: add shadow on edges for better UX */
  box-shadow: inset 0 0 0 rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    margin-bottom: 1rem; /* give some breathing room on mobile */
  }
`;
const DateWrapper = styled.div<{ $invalid?: boolean }>`
  border: 1px solid ${({ $invalid }) => ($invalid ? "red" : "white")};
  border-radius: 6px;
  padding: 0.3rem;
  margin-bottom: 0.3rem;
`;

export const SectionWrapper = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1.2rem;
  margin-top: 1.5rem;
  background: #fafafa;
`;

export const SectionHeading = styled.h4`
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #094752;
`;

export const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const DetailsBtn = styled.button`
  background-color: #1976d2;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 6px;
`;

const ReleaseNotesBox = styled.div`
  background-color: #f9f9f9;
  border-left: 3px solid #1976d2;
  margin-top: 6px;
  margin-bottom: 10px;
  padding: 10px 12px;
  font-size: 14px;
  width: 150%;
  text-align: left;
`;

const Required = styled.span`
  color: red;
  margin-left: 2px;
  font-weight: bold;
`;

const ActionIcons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const IconBtn = styled.button<{ danger?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: ${({ danger }) => (danger ? "#ff4d4f" : "#1976d2")};
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    color: ${({ danger }) => (danger ? "#d9363e" : "#0d47a1")};
    transform: scale(1.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
const SmallModalBox = styled(ModalBox)`
  max-width: 420px;
  width: 90%;
  padding: 1.2rem;
`;
